import {
  GroupedBy,
  ItemType,
  PricingModel,
  QuantityMetaType,
  SubscriberCTAAction,
  type Currency,
  type Frequency,
  type Item,
  type Price,
  type PricingTable,
  type QuantityMeta,
  type Subscription,
} from '@bbkPricingPage/types/pricing-table-types';

type EntityWithOrder = {
  display_order?: number;
};

export type ActiveItemPrice = {
  item: Item;
  price: Price;
  isSubscribedItem: boolean;
  isSubscribedPrice: boolean;
};

export const EDITOR_PREVIEW_SUBSCRIPTION_ID = 'editor-preview-subscription';

const SubscriptionStatus = {
  future: 'future',
  in_trial: 'in_trial',
  active: 'active',
  non_renewing: 'non_renewing',
  paused: 'paused',
  cancelled: 'cancelled',
  transferred: 'transferred',
} as const;

export const disablingStatuses = [
  SubscriptionStatus.future,
  SubscriptionStatus.non_renewing,
  SubscriptionStatus.transferred,
  SubscriptionStatus.paused,
] as const;

export const validStatuses = [
  SubscriptionStatus.in_trial,
  SubscriptionStatus.cancelled,
] as const;

const CustomizePlanCTAText = {
  in_trial: 'You are currently on trial',
  cancelled: 'You are currently on this plan',
};

export const getSubscribedCTAText = (
  existingText: string | undefined,
  subscriptionStatus?: string
) => {
  if (
    validStatuses.includes(subscriptionStatus as (typeof validStatuses)[number])
  ) {
    return CustomizePlanCTAText[
      subscriptionStatus as (typeof validStatuses)[number]
    ];
  }
  return existingText;
};

type SubscriptionStatusType =
  (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

/** Matches Atomic Pricing: quantity upgrades require a plan quantity slider. */
export const hasQuantitySlider = (quantityMeta?: QuantityMeta): boolean =>
  quantityMeta?.type === QuantityMetaType.SLIDER;

export const isCTADisabled = (
  subscriptionStatus: string | undefined,
  subscriptionQuantity: number,
  isSubscribedPrice: boolean,
  subscriberCTAAction: SubscriberCTAAction,
  quantity: number,
  hasChanges: boolean,
  price: Price,
  quantityMeta?: QuantityMeta
) => {
  const isDisablingStatus = (disablingStatuses as readonly string[]).includes(
    subscriptionStatus as SubscriptionStatusType
  );
  if (isDisablingStatus) return true;

  if (!isSubscribedPrice) return false;

  // Active / in_trial
  if (
    subscriptionStatus === SubscriptionStatus.active ||
    subscriptionStatus === SubscriptionStatus.in_trial
  ) {
    if (subscriberCTAAction === SubscriberCTAAction.do_nothing) {
      return true;
    }
    if (subscriberCTAAction === SubscriberCTAAction.redirect_to_url) {
      return false;
    }
    if (subscriberCTAAction === SubscriberCTAAction.allow_quantity_upgrades) {
      // Only enable on a strict quantity increase for non-flat-fee prices
      if (
        price.pricing_model !== PricingModel.flat_fee &&
        quantity &&
        subscriptionQuantity
      ) {
        if (!hasQuantitySlider(quantityMeta)) {
          return true;
        }
        if (quantity > subscriptionQuantity) {
          return false;
        } else {
          return true;
        }
      } else {
        return !hasChanges;
      }
    }
    return false;
  }
  // Cancelled
  if (subscriptionStatus === SubscriptionStatus.cancelled) {
    if (subscriberCTAAction === SubscriberCTAAction.do_nothing) {
      return true;
    }
    if (subscriberCTAAction === SubscriberCTAAction.redirect_to_url) {
      return false;
    }
    if (subscriberCTAAction === SubscriberCTAAction.allow_quantity_upgrades) {
      if (
        price.pricing_model !== PricingModel.flat_fee &&
        quantity &&
        subscriptionQuantity
      ) {
        if (!hasQuantitySlider(quantityMeta)) {
          return true;
        }
        if (quantity > subscriptionQuantity) {
          return false;
        }
        return true;
      } else {
        return false;
      }
    }
    return false;
  }
  return true;
};

export class PricingTableHelper {
  public static getId = (item: { billing_system_id: string }) =>
    item?.billing_system_id?.toLowerCase() ?? '';

  public static getItemId = (item: { billing_system_item_id: string }) =>
    item?.billing_system_item_id?.toLowerCase() ?? '';

  public static getIds = (items?: { billing_system_id: string }[]) =>
    items?.map((i) => i.billing_system_id) ?? [];

  public static getApplicablePriceVariant = (
    items?: Item[],
    subscription?: Subscription
  ): string | undefined => {
    const subscriptionPlan = this.getSubscribedPlan(subscription);
    return items
      ?.filter((item) => item.is_attached)
      .flatMap((item) => item.prices ?? [])
      .find(
        (price) =>
          price.billing_system_id ===
          subscriptionPlan?.billing_system_item_price_id
      )?.price_variant_id;
  };

  public static getActiveItemPrices = (
    items?: Item[],
    currency?: Currency,
    frequency?: Frequency,
    priceVariantId?: string | null,
    groupedBy?: GroupedBy,
    hideSubscribedPlan?: boolean,
    subscription?: Subscription
  ) => {
    const subscriptionPlan = this.getSubscribedPlan(subscription);

    return items
      ?.filter((item) => item.is_attached)
      .sort(this.itemsComparator)
      .map((item) => {
        const prices = (item.prices ?? [])
          .filter(
            (price) =>
              currency &&
              price.currency === currency.currency_code &&
              price.active &&
              (groupedBy === GroupedBy.item_frequency
                ? true
                : frequency &&
                  price.period_unit === frequency.period_unit &&
                  price.period === frequency.period) &&
              (price.price_variant_id == null ||
                price.price_variant_id == priceVariantId) // null and undefined are treated as same
          )
          .sort((price) => (price.price_variant_id == priceVariantId ? -1 : 1));

        const activePrice = prices.length >= 1 ? prices[0] : undefined;
        return {
          item,
          price: activePrice,
          isSubscribedItem: (item.prices ?? []).some(
            (price) =>
              price.billing_system_id ===
              subscriptionPlan?.billing_system_item_price_id
          ),
          isSubscribedPrice:
            subscriptionPlan?.billing_system_item_price_id ===
            activePrice?.billing_system_id,
        };
      })
      .filter((activeItem) => {
        if (hideSubscribedPlan && activeItem.isSubscribedPrice) {
          return false;
        }
        return activeItem.price !== undefined;
      }) as ActiveItemPrice[];
  };

  public static hasBadge = (item: Item) => {
    return !!(
      item?.customization.badge &&
      item.customization.badge.type !== 'none' &&
      item.customization.badge.text
    );
  };

  public static generatePriceSuffix = (
    frequency: Frequency,
    isCharge?: boolean
  ) => {
    if (isCharge) {
      if (frequency.show_monthly_price && frequency.price_suffix) {
        return frequency.price_suffix;
      }
      return `/One-time`;
    }
    if (frequency.price_suffix) return frequency.price_suffix;

    if (frequency.period === 1) {
      return `per ${frequency.period_unit}`;
    }
    return `per ${frequency.period} ${frequency.period_unit}`;
  };

  static getSubscribedPlan = (subscription?: Subscription) => {
    return subscription?.subscription_items?.filter(
      (item) => item.item_type === ItemType.plan
    )?.[0];
  };

  /**
   * Mock subscription for the pricing page editor's inline preview when the
   * Subscriber tab is selected.
   *
   * Mirrors Atomic Pricing's findFirstAttachedItemPrice: the preview anchors on
   * the first attached price walking items (left-to-right by display order) and
   * their prices in order, NOT by currency display order. The chosen price's
   * currency/frequency then drive the preview, matching how AP narrows the
   * subscriber view to the subscription's own currency and billing period.
   */
  static buildEditorPreviewSubscription = (
    pricingTable?: PricingTable,
    priceVariantId?: string | null
  ): Subscription | undefined => {
    if (!pricingTable?.items?.length) {
      return undefined;
    }

    const resolvedVariantId =
      priceVariantId && priceVariantId !== 'default' ? priceVariantId : null;

    const currencies =
      PricingTableHelper.getCurrenciesFromPricingTable(pricingTable);
    const frequencies =
      PricingTableHelper.getFrequenciesFromPricingTable(pricingTable);
    if (currencies.length === 0) {
      return undefined;
    }

    const applicableCurrencyCodes = new Set(
      currencies.map((currency) => currency.currency_code)
    );
    const applicableFrequencyKeys = new Set(
      frequencies.map(
        (frequency) => `${frequency.period}::${frequency.period_unit}`
      )
    );

    const anchorPrice = pricingTable.items
      .filter((item) => item.is_attached === true || item.is_attached === null)
      .sort(PricingTableHelper.itemsComparator)
      .flatMap((item) => item.prices ?? [])
      .find(
        (price) =>
          price.is_attached &&
          price.active &&
          price.period != null &&
          price.period_unit != null &&
          applicableFrequencyKeys.has(
            `${price.period}::${price.period_unit}`
          ) &&
          applicableCurrencyCodes.has(price.currency) &&
          (price.price_variant_id == null ||
            price.price_variant_id === resolvedVariantId)
      );

    if (!anchorPrice) {
      return undefined;
    }

    const currency = currencies.find(
      (c) => c.currency_code === anchorPrice.currency
    );
    const frequency =
      pricingTable.grouped_by === GroupedBy.item_frequency
        ? undefined
        : frequencies.find(
            (f) =>
              f.period === anchorPrice.period &&
              f.period_unit === anchorPrice.period_unit
          );

    // Resolve the price that actually renders for that currency/frequency so the
    // subscribed-plan highlight respects variant/visibility rules at runtime.
    const price =
      PricingTableHelper.getActiveItemPrices(
        pricingTable.items,
        currency,
        frequency,
        resolvedVariantId,
        pricingTable.grouped_by,
        false,
        undefined
      ).find((entry) => entry.price)?.price ?? anchorPrice;

    return {
      id: EDITOR_PREVIEW_SUBSCRIPTION_ID,
      customer_id: 'editor-preview-customer',
      currency: price.currency,
      status: SubscriptionStatus.active as Subscription['status'],
      period: price.period,
      period_unit: price.period_unit,
      subscription_items: [
        {
          billing_system_item_price_id: price.billing_system_id,
          item_type: ItemType.plan,
          quantity: 1,
        },
      ],
    };
  };

  static itemsComparator = (item1: EntityWithOrder, item2: EntityWithOrder) => {
    return (item1.display_order ?? -1) - (item2.display_order ?? -1);
  };

  static getFrequenciesFromPricingTable = (
    pricingTable: PricingTable | undefined
  ): Frequency[] => {
    if (!pricingTable) {
      return [];
    }
    const seen = new Set<string>();
    const existingFrequencies = pricingTable.frequencies || [];
    pricingTable.items
      ?.filter((item) => item.is_attached === true || item.is_attached === null)
      .forEach((item) => {
        item.prices
          ?.filter((price) => price.is_attached === true)
          .forEach((price) => {
            if (price.period != null && price.period_unit != null) {
              seen.add(`${price.period}::${price.period_unit}`);
            }
          });
      });

    return existingFrequencies
      .filter((f) => f.is_attached && seen.has(`${f.period}::${f.period_unit}`))
      .sort(this.itemsComparator);
  };

  static getCurrenciesFromPricingTable = (
    pricingTable: PricingTable | undefined
  ): Currency[] => {
    const currencies = new Set<string>();
    if (!pricingTable) {
      return [];
    }
    const existingCurrencies = pricingTable.currencies || [];
    pricingTable.items
      ?.filter((item) => item.is_attached === true || item.is_attached === null)
      .forEach((item) => {
        item.prices
          ?.filter((price) => price.is_attached === true)
          .forEach((price) => {
            currencies.add(price.currency);
          });
      });

    const result: Currency[] = Array.from(currencies)
      .map((currency) => {
        const existingCurrency = existingCurrencies.find(
          (c) => c.currency_code === currency
        ) as Currency;
        return {
          ...existingCurrency,
          currency_code: currency,
        };
      })
      .sort(this.itemsComparator)
      .filter((item) => item.is_attached === true);
    return result;
  };
}

// Returns the slider default clamped to [min, max];
export const getDefaultValueFromQuantityMeta = (
  quantityMeta: QuantityMeta | undefined
): number => {
  if (!quantityMeta) {
    return 1;
  }
  const fallback = quantityMeta.default_value ?? quantityMeta.min ?? 1;
  return Math.min(Math.max(quantityMeta.min, fallback), quantityMeta.max);
};

// Prefers price-level quantity_meta, falls back to item-level for non-flat-fee.
export const resolveQuantityMeta = (
  item: Item,
  price: Price
): QuantityMeta | undefined => {
  let meta = price.quantity_meta;
  if (!meta && price.pricing_model !== PricingModel.flat_fee) {
    meta = item.customization.quantity_meta;
  }
  return meta;
};

export const resolvePlanQuantity = ({
  priceId,
  priceQuantities,
  initQuantity,
  isSubscribedPrice,
  subscribedPlanQuantity,
  quantityMeta,
}: {
  priceId: string;
  priceQuantities: Record<string, number>;
  initQuantity?: number;
  isSubscribedPrice: boolean;
  subscribedPlanQuantity?: number;
  quantityMeta?: QuantityMeta;
}): number => {
  const perPriceQuantity = priceQuantities[priceId];
  if (perPriceQuantity != null) {
    return perPriceQuantity;
  }
  if (isSubscribedPrice && subscribedPlanQuantity && quantityMeta) {
    return subscribedPlanQuantity;
  }
  if (initQuantity) {
    return initQuantity;
  }
  if (quantityMeta?.default_value != null) {
    return getDefaultValueFromQuantityMeta(quantityMeta);
  }
  return quantityMeta ? getDefaultValueFromQuantityMeta(quantityMeta) : 1;
};

// Returns the frequency matching `price`; when subscribed and
// `hideFrequencySwitch` is set, restricts matches to the subscription frequency.
export const getApplicableFrequency = (
  price: Price,
  subscription?: Subscription,
  frequencies?: Frequency[],
  hideFrequencySwitch?: boolean
): Frequency | undefined => {
  return frequencies
    ?.filter((freq) =>
      subscription && hideFrequencySwitch
        ? freq.period === subscription?.period &&
          freq.period_unit === subscription?.period_unit
        : true
    )
    .find(
      (freq) =>
        freq.period_unit === price.period_unit && freq.period === price.period
    );
};
